if (!customElements.get('mz-product-configurator')) {
  customElements.define(
    'mz-product-configurator',
    class MzProductConfigurator extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.abortController = new AbortController();
        this.signal = this.abortController.signal;
        this.tabs = Array.from(this.querySelectorAll('[data-config-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-config-panel]'));
        this.choicePanels = this.panels.filter((panel) => !panel.hasAttribute('data-config-summary-panel'));
        this.options = Array.from(this.querySelectorAll('[data-config-option]'));
        this.previousButton = this.querySelector('[data-config-prev]');
        this.nextButton = this.querySelector('[data-config-next]');
        this.navigation = this.querySelector('.mz-configurator__navigation');
        this.previousLabel = this.querySelector('[data-config-prev-label]');
        this.nextLabel = this.querySelector('[data-config-next-label]');
        this.buyButton = this.querySelector('[data-config-buy]');
        this.financingButton = this.querySelector('[data-mz-financing-open]');
        this.totalTargets = Array.from(this.querySelectorAll('[data-config-total]'));
        this.summaryTarget = this.querySelector('[data-config-summary]');
        this.summaryPanel = this.querySelector('[data-config-summary-panel]');
        this.statusTarget = this.querySelector('[data-config-status]');
        this.announcer = this.querySelector('[data-config-announcer]');
        this.currentTarget = this.querySelector('[data-config-current]');
        this.currentLabelTarget = this.querySelector('[data-config-current-label]');
        this.progressTarget = this.querySelector('[data-config-progress]');
        this.progressBar = this.querySelector('[data-config-progress-bar]');
        this.summaryImage = this.querySelector('.mz-configurator__summary-media img');
        this.originalSummaryImage = this.summaryImage ? this.summaryImage.currentSrc || this.summaryImage.src : '';
        this.visualImage = this.querySelector('.mz-configurator-modal__visual-media img');
        this.originalVisualImage = this.visualImage ? this.visualImage.currentSrc || this.visualImage.src : '';
        this.basePrice = Number(this.dataset.basePrice || 0);
        this.baseAvailable = this.dataset.baseAvailable !== 'false';
        this.currency = this.dataset.currency || 'CHF';
        this.activeIndex = 0;
        this.currentTotal = this.basePrice;
        this.confirmedSteps = new Set();
        this.initialSelections = new Map(
          this.choicePanels.map((panel) => [
            panel,
            this.selectedOptions(panel)
              .map((option) => option.dataset.label || '')
              .sort()
              .join('|'),
          ])
        );

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.setStep(index), { signal: this.signal });
          tab.addEventListener('keydown', (event) => this.onTabKeydown(event, index), { signal: this.signal });
        });

        this.options.forEach((option) => {
          option.addEventListener('click', () => this.selectOption(option), { signal: this.signal });
        });

        this.previousButton?.addEventListener('click', () => this.goPrevious(), { signal: this.signal });
        this.nextButton?.addEventListener('click', () => this.goNext(), { signal: this.signal });
        this.buyButton?.addEventListener('click', () => this.addConfigurationToCart(), { signal: this.signal });
        this.financingButton?.addEventListener('click', () => this.openFinancing(), { signal: this.signal });
        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const selectedPanel = event.target.closest?.('[data-config-panel]');
            const selectedIndex = this.panels.indexOf(selectedPanel);
            if (selectedIndex >= 0) this.setStep(selectedIndex, false);
          },
          { signal: this.signal }
        );

        this.setStep(0, false, false);
        this.updateSummary();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.initialized = false;
      }

      money(cents) {
        if (this.currency === 'CHF') {
          const value = Math.max(0, Number(cents) || 0);
          const whole = Math.floor(value / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
          const decimals = String(value % 100).padStart(2, '0');
          return `CHF ${whole}${decimals === '00' ? '' : `.${decimals}`}`;
        }
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
            style: 'currency',
            currency: this.currency,
          }).format((Number(cents) || 0) / 100);
        } catch (error) {
          return `${this.currency} ${((Number(cents) || 0) / 100).toFixed(2)}`;
        }
      }

      onTabKeydown(event, index) {
        let targetIndex = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = this.tabs.length - 1;
        if (targetIndex === index) return;
        event.preventDefault();
        this.setStep(targetIndex);
        this.tabs[targetIndex]?.focus();
      }

      setStep(index, scroll = true, announce = true) {
        if (!this.tabs.length) return;
        const nextIndex = Math.max(0, Math.min(index, this.tabs.length - 1));
        this.activeIndex = nextIndex;

        this.tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === this.activeIndex;
          const panel = this.panels[tabIndex];
          const complete = this.confirmedSteps.has(tabIndex) && !active;
          const selected = panel && !panel.hasAttribute('data-config-summary-panel') && this.selectedOptions(panel).length > 0;
          const currentSelection = selected
            ? this.selectedOptions(panel)
                .map((option) => option.dataset.label || '')
                .sort()
                .join('|')
            : '';
          const changed = selected && currentSelection !== this.initialSelections.get(panel);
          tab.classList.toggle('is-active', active);
          tab.classList.toggle('is-complete', complete);
          tab.classList.toggle('is-upcoming', !active && !complete);
          tab.classList.toggle('has-selection', selected && !complete);
          tab.setAttribute('aria-selected', String(active));
          if (active) tab.setAttribute('aria-current', 'step');
          else tab.removeAttribute('aria-current');
          tab.tabIndex = active ? 0 : -1;
          const status = tab.querySelector('[data-config-tab-status]');
          if (status) status.textContent = active ? 'Aktuell' : complete ? 'Erledigt' : selected ? (changed ? 'Ausgewählt' : 'Standard') : 'Offen';
        });

        this.panels.forEach((panel, panelIndex) => {
          panel.hidden = panelIndex !== this.activeIndex;
        });

        const currentNumber = this.activeIndex + 1;
        const currentTitle = this.panels[this.activeIndex]?.dataset.stepTitle || '';
        const progress = Math.round((currentNumber / this.tabs.length) * 100);
        if (this.currentTarget) this.currentTarget.textContent = String(currentNumber);
        if (this.currentLabelTarget) this.currentLabelTarget.textContent = currentTitle;
        if (this.progressTarget) this.progressTarget.setAttribute('aria-valuenow', String(currentNumber));
        if (this.progressBar) this.progressBar.style.width = `${progress}%`;

        if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
        if (this.previousLabel) {
          this.previousLabel.textContent = this.activeIndex > 0 ? this.panels[this.activeIndex - 1]?.dataset.stepTitle || 'Zurück' : 'Zurück';
        }
        if (this.nextButton) this.nextButton.hidden = this.activeIndex === this.tabs.length - 1;
        this.navigation?.classList.toggle('is-summary', this.activeIndex === this.tabs.length - 1);
        if (this.nextLabel && this.activeIndex < this.tabs.length - 1) {
          this.nextLabel.textContent = this.panels[this.activeIndex + 1]?.dataset.stepTitle || 'Weiter';
        }

        if (announce && this.announcer) this.announcer.textContent = `Schritt ${currentNumber} von ${this.tabs.length}: ${currentTitle}`;
        if (scroll) {
          const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
          const panel = this.panels[this.activeIndex];
          window.setTimeout(() => {
            panel?.querySelector('[data-config-panel-focus]')?.focus({ preventScroll: true });
            if (matchMedia('(max-width: 749px)').matches) {
              panel?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
            }
          }, reduceMotion ? 0 : 120);
        }
        this.setStatus('');
      }

      goPrevious() {
        if (this.activeIndex > 0) this.setStep(this.activeIndex - 1);
      }

      goNext() {
        if (this.activeIndex >= this.tabs.length - 1) return;
        const panel = this.panels[this.activeIndex];
        if (panel && !panel.hasAttribute('data-config-summary-panel') && !this.currentStepIsComplete(panel)) {
          this.setStatus('Bitte wählen Sie eine Option, bevor Sie fortfahren.', true);
          panel.querySelector('[data-config-option]:not(:disabled)')?.focus();
          return;
        }
        if (panel && !panel.hasAttribute('data-config-summary-panel')) this.confirmedSteps.add(this.activeIndex);
        this.setStep(this.activeIndex + 1);
      }

      currentStepIsComplete(panel) {
        if (!panel || panel.dataset.selectionType === 'multiple') return true;
        return this.selectedOptions(panel).length > 0;
      }

      selectOption(option) {
        if (option.disabled) return;
        const stepKey = option.dataset.stepKey;
        const multiple = option.dataset.selectionType === 'multiple';
        if (multiple) {
          option.classList.toggle('is-selected');
        } else {
          this.options
            .filter((candidate) => candidate.dataset.stepKey === stepKey)
            .forEach((candidate) => candidate.classList.remove('is-selected'));
          option.classList.add('is-selected');
        }
        this.options.forEach((candidate) => {
          candidate.setAttribute('aria-pressed', String(candidate.classList.contains('is-selected')));
        });
        if (option.classList.contains('is-selected') && option.dataset.previewImage && this.summaryImage) {
          this.summaryImage.src = option.dataset.previewImage;
          this.summaryImage.removeAttribute('srcset');
        }
        if (option.classList.contains('is-selected') && option.dataset.previewImage && this.visualImage) {
          this.visualImage.src = option.dataset.previewImage;
          this.visualImage.removeAttribute('srcset');
        }
        this.updateSummary();
        this.setStep(this.activeIndex, false, false);
      }

      openAt({ stepKey = '', optionLabel = '' } = {}) {
        const normalizedStep = String(stepKey).trim().toLocaleLowerCase(document.documentElement.lang || 'de-CH');
        const stepIndex = normalizedStep
          ? this.tabs.findIndex(
              (tab) => String(tab.dataset.stepKey || '').trim().toLocaleLowerCase(document.documentElement.lang || 'de-CH') === normalizedStep
            )
          : 0;
        this.setStep(stepIndex >= 0 ? stepIndex : 0, false, false);

        if (optionLabel) {
          const normalizedOption = String(optionLabel).trim().toLocaleLowerCase(document.documentElement.lang || 'de-CH');
          const option = this.options.find(
            (candidate) =>
              String(candidate.dataset.stepKey || '').trim().toLocaleLowerCase(document.documentElement.lang || 'de-CH') === normalizedStep &&
              String(candidate.dataset.label || '').trim().toLocaleLowerCase(document.documentElement.lang || 'de-CH') === normalizedOption
          );
          if (option && !option.disabled) this.selectOption(option);
        }

        window.setTimeout(() => this.panels[this.activeIndex]?.querySelector('[data-config-panel-focus]')?.focus({ preventScroll: true }), 80);
      }

      openFinancing(trigger = this.financingButton) {
        const productRoot = this.closest('product-info') || document;
        const financing = productRoot.querySelector('leasing-flow');
        if (!financing) return;
        const configuration = this.choicePanels
          .map((panel) => {
            const labels = this.selectedOptions(panel).map((option) => option.dataset.label);
            return labels.length ? `${panel.dataset.stepTitle}: ${labels.join(', ')}` : '';
          })
          .filter(Boolean)
          .join(' · ');
        if (typeof financing.open === 'function') {
          const configuratorModal = this.closest('modal-dialog[data-mz-configurator-modal]');
          const modalWasOpen = configuratorModal?.hasAttribute('open');
          if (modalWasOpen && typeof configuratorModal.hide === 'function') configuratorModal.hide();
          window.setTimeout(
            () => financing.open({ trigger, priceCents: this.currentTotal, configuration }),
            modalWasOpen ? 80 : 0
          );
        }
      }

      selectedOptions(panel) {
        return Array.from(panel.querySelectorAll('[data-config-option].is-selected'));
      }

      configurationIsComplete() {
        return this.choicePanels.every((panel) => this.currentStepIsComplete(panel));
      }

      updateSummary() {
        let total = this.basePrice;
        if (this.summaryTarget) this.summaryTarget.replaceChildren();

        this.choicePanels.forEach((panel) => {
          const selected = this.selectedOptions(panel);
          const stepTitle = panel.dataset.stepTitle || '';
          selected.forEach((option) => {
            total += Number(option.dataset.price || 0);
          });

          if (!this.summaryTarget) return;
          const row = document.createElement('div');
          row.className = 'mz-configurator__selection-row';
          const key = document.createElement('span');
          key.textContent = stepTitle;
          const value = document.createElement('strong');
          value.textContent = selected.length ? selected.map((option) => option.dataset.label).join(', ') : 'Keine Zusatzoption';
          row.append(key, value);
          this.summaryTarget.append(row);
        });

        this.currentTotal = total;
        const formattedTotal = this.money(total);
        this.totalTargets.forEach((target) => {
          target.textContent = formattedTotal;
        });
        this.dispatchEvent(
          new CustomEvent('mz:configurator-total-change', {
            bubbles: true,
            detail: { cents: total, formatted: formattedTotal },
          })
        );
        if (this.buyButton) {
          this.buyButton.disabled = !this.baseAvailable || !this.configurationIsComplete() || this.buyButton.dataset.loading === 'true';
        }
        this.setStatus('');
      }

      buildCartItems() {
        const baseVariantId = Number(this.dataset.baseVariantId || 0);
        if (!baseVariantId) throw new Error('Die Produktvariante ist nicht verfügbar.');
        if (!this.configurationIsComplete()) throw new Error('Bitte treffen Sie in jedem Pflichtschritt eine Auswahl.');

        const bundleKey = `mz-configurator-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const properties = {
          Konfiguration: 'MotoZero Konfigurator',
          _stark_configurator_base: 'true',
          _stark_configurator_bundle: bundleKey,
        };
        const selected = this.options.filter((option) => option.classList.contains('is-selected'));
        const addOns = new Map();

        this.choicePanels.forEach((panel) => {
          const labels = this.selectedOptions(panel).map((option) => option.dataset.label);
          if (labels.length) properties[panel.dataset.stepTitle || panel.dataset.stepKey] = labels.join(', ');
        });

        const previewOption = selected.find((option) => option.dataset.previewImage);
        if (previewOption) properties._stark_configurator_preview_image = previewOption.dataset.previewImage;

        selected.forEach((option) => {
          const variantId = Number(option.dataset.variantId || 0);
          if (!variantId) return;
          if (!addOns.has(variantId)) {
            addOns.set(variantId, {
              id: variantId,
              quantity: 1,
              properties: {
                _stark_configurator_addon: 'true',
                _stark_configurator_bundle: bundleKey,
                _stark_configurator_selection: option.dataset.label || '',
                _stark_configurator_parent_variant: String(baseVariantId),
                _stark_configurator_preview_image: option.dataset.previewImage || '',
              },
            });
          } else {
            addOns.get(variantId).quantity += 1;
          }
        });

        return [...addOns.values(), { id: baseVariantId, quantity: 1, properties }];
      }

      async addConfigurationToCart() {
        if (!this.buyButton || this.buyButton.dataset.loading === 'true') return;
        const originalLabel = this.buyButton.textContent;
        try {
          const items = this.buildCartItems();
          this.buyButton.dataset.loading = 'true';
          this.buyButton.disabled = true;
          this.buyButton.textContent = this.dataset.loadingLabel || 'Wird hinzugefügt …';
          this.setStatus('');

          const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload.description || payload.message || this.dataset.errorLabel);
          window.location.assign(this.dataset.cartUrl || '/cart');
        } catch (error) {
          this.buyButton.dataset.loading = 'false';
          this.buyButton.textContent = originalLabel;
          this.buyButton.disabled = !this.baseAvailable || !this.configurationIsComplete();
          this.setStatus(error.message || this.dataset.errorLabel, true);
        }
      }

      setStatus(message, isError = false) {
        if (!this.statusTarget) return;
        this.statusTarget.textContent = message || '';
        this.statusTarget.classList.toggle('is-error', Boolean(message && isError));
      }
    }
  );
}
